import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/providers/ToastProvider';
import { useMemedPrescription, extractMemedPrescriptionId, extractMemedPrescriptionData } from '@/hooks/useMemedPrescription';
import { useMemedConfig } from '@/hooks/useMemedConfig';
import { memedApi, medicalRecordsApi, examsHubApi } from '@/api';
import { mapPatientToMemed, validateMemedPatient } from '@/hooks/memed/memed-patient.mapper';
import { obterWorkplaceParaAtendimento } from '@/hooks/memed/workplace-router';
import {
  Pill,
  FileText,
  Activity,
  Check,
  ExternalLink,
  Copy,
  Sparkles,
  Clock,
  AlertCircle,
  Plus,
  Trash2,
  CheckCircle2,
} from 'lucide-react';

export interface PatientModalInfo {
  id: string;
  name: string;
  cpf?: string | null;
  email?: string | null;
  phone?: string | null;
  gender?: string | null;
  birthDate?: string | null;
}

export interface TelemedicinePrescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: PatientModalInfo | null;
  doctorId: string;
  appointmentId?: string;
  initialTab?: 'prescription' | 'certificate' | 'exam';
  onDocumentIssued?: (doc: {
    type: 'prescription' | 'certificate' | 'exam';
    id: string;
    link?: string;
    unlockCode?: string;
    title: string;
  }) => void;
}

const COMMON_EXAMS = [
  'Hemograma Completo',
  'Glicemia de Jejum',
  'Lipidograma Completo',
  'Creatinina Sérica',
  'Ureia Sérica',
  'Urina Tipo 1 (EAS)',
  'TGO e TGP (Transaminases)',
  'TSH e T4 Livre',
  'Raio-X de Tórax (PA e Perfil)',
  'Eletrocardiograma (ECG)',
  'Ultrassonografia de Abdome Total',
];

export function TelemedicinePrescriptionModal({
  isOpen,
  onClose,
  patient,
  doctorId,
  appointmentId,
  initialTab = 'prescription',
  onDocumentIssued,
}: TelemedicinePrescriptionModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { openPrescription, onMemedEvent, loadScript, isScriptLoaded, isModuleReady } = useMemedPrescription();
  const { data: memedConfig } = useMemedConfig();

  const [activeTab, setActiveTab] = useState<'prescription' | 'certificate' | 'exam'>(initialTab);
  const [isOpeningMemed, setIsOpeningMemed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Form states - Receita
  const [rxType, setRxType] = useState<'COMUM' | 'CONTROLADA'>('COMUM');
  const [rxInstructions, setRxInstructions] = useState('');

  // Form states - Atestado
  const [daysOff, setDaysOff] = useState<number>(1);
  const [certStartDate, setCertStartDate] = useState(() => new Date().toISOString().split('T')[0] || '');
  const [certReason, setCertReason] = useState('Necessidade de repouso por quadro clínico agudo');
  const [cidCode, setCidCode] = useState('');
  const [cidDescription, setCidDescription] = useState('');

  // Form states - Exames
  const [selectedExams, setSelectedExams] = useState<string[]>(['Hemograma Completo']);
  const [customExamInput, setCustomExamInput] = useState('');
  const [examJustification, setExamJustification] = useState('Avaliação clínica diagnóstica');

  // Estado do documento emitido com sucesso
  const [issuedDoc, setIssuedDoc] = useState<{
    type: 'prescription' | 'certificate' | 'exam';
    title: string;
    link?: string;
    unlockCode?: string;
    id: string;
  } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, isOpen]);

  // Listener para capturar a assinatura digital oficial MeMed (ICP-Brasil)
  useEffect(() => {
    if (!isOpen) return;

    const unsubscribePrinted = onMemedEvent('prescricaoImpressa', async (data) => {
      console.info('[TelemedicinePrescriptionModal] ✓ Evento prescricaoImpressa capturado:', data);
      const extracted = extractMemedPrescriptionData(data);
      const memedId = extracted.memedPrescriptionId || extractMemedPrescriptionId(data);

      if (!memedId || !patient) return;

      try {
        const tokenRes = await memedApi.getToken().catch(() => ({ token: undefined }));
        const doctorToken = tokenRes?.token;

        let docTitle = 'Receita Médica Digital';
        let digitalLink = extracted.digitalPrescriptionLink;
        let unlockCode = extracted.unlockCode;

        if (activeTab === 'certificate') {
          const mentions = (data as any)?.prescrição?.additionalData?.mentions || (data as any)?.additionalData?.mentions || {};
          const finalDays = Number(mentions.dias_afastamento) || daysOff || 1;
          const finalCid = mentions.CID || mentions.cid || cidCode || undefined;
          const finalReason = cidDescription || certReason;
          const finalDiagnosis = finalCid ? `${finalCid} - ${finalReason}` : finalReason;

          docTitle = `Atestado Médico (${finalDays} dias)`;
          await memedApi.syncMemedCertificate({
            appointmentId: appointmentId || undefined,
            patientId: patient.id,
            doctorId,
            memedPrescriptionId: memedId,
            diagnosis: finalDiagnosis,
            totalDays: finalDays,
            diasAfastamento: finalDays,
            cidCodigo: finalCid,
            tipoDocumento: 'TRABALHO',
            textoFormatado: certReason,
            horarioAtendimento: mentions.horario_atendimento || undefined,
            nomeAcompanhante: mentions.nome_acompanhante || mentions.acompanhante || undefined,
            startDate: mentions.data_afastamento || certStartDate || new Date().toISOString(),
            doctorToken,
            pdfUrl: (data as any)?.pdfUrl,
            assinadoDigitalmente: true,
            mentions,
          });
        } else if (activeTab === 'exam') {
          docTitle = `Pedido de Exames (${selectedExams.join(', ') || 'Exames'})`;
          const examesFromMemed = ((data as any)?.prescrição?.medicamentos || []).filter(
            (item: any) => item.tipo === 'exame' || item.custom_categoria === 'exame',
          );

          const itemsToIngest =
            examesFromMemed.length > 0
              ? examesFromMemed.map((e: any) => ({
                  memedId: e.id ? String(e.id) : undefined,
                  nome: e.nome,
                  tussCodigo: e.exames_tuss_codigo || null,
                  susCodigo: e.exames_sus_codigo || null,
                  indicacaoClinica: e.sanitized_posology || e.posologia || examJustification,
                }))
              : selectedExams.map((e) => ({
                  nome: e,
                  indicacaoClinica: examJustification,
                }));

          try {
            await examsHubApi.ingestExams({
              pacienteId: patient.id,
              medicoId: doctorId,
              appointmentId: appointmentId || undefined,
              prescriptionUuid: memedId,
              pdfUrl: (data as any)?.pdfUrl,
              signed: true,
              exames: itemsToIngest,
            });
          } catch (err) {
            console.warn('[TelemedicinePrescriptionModal] Falha ao enviar para o hub de exames:', err);
          }

          await memedApi.syncMemedExam({
            appointmentId: appointmentId || undefined,
            patientId: patient.id,
            doctorId,
            memedPrescriptionId: memedId,
            exams: selectedExams.map((e) => ({
              name: e,
              examName: e,
              justification: examJustification,
            })),
            type: selectedExams.join(', ') || 'Exame Complementar',
            justification: examJustification,
            doctorToken,
            pdfUrl: (data as any)?.pdfUrl,
          });
        } else {
          docTitle = rxType === 'CONTROLADA' ? 'Receita Controlada (C1/B1)' : 'Receita Médica Simples';
          const syncResult = await medicalRecordsApi.syncMemedPrescription({
            appointmentId: appointmentId || undefined,
            medicalRecordId: appointmentId || undefined,
            patientId: patient.id,
            doctorId,
            memedPrescriptionId: memedId,
            medications: extracted.medications,
            digitalPrescriptionLink: extracted.digitalPrescriptionLink,
            unlockCode: extracted.unlockCode,
          });

          if (!digitalLink || !unlockCode) {
            try {
              const linkRes = await medicalRecordsApi.getDigitalPrescriptionLink(syncResult?.id || memedId);
              if (linkRes?.link) digitalLink = linkRes.link;
              if (linkRes?.unlockCode) unlockCode = linkRes.unlockCode;
            } catch {
              // fallback gracioso
            }
          }
        }

        queryClient.invalidateQueries({ queryKey: ['doctor', 'prescriptions'] });
        queryClient.invalidateQueries({ queryKey: ['medical-records'] });
        queryClient.invalidateQueries({ queryKey: ['doctor', 'certificates'] });
        queryClient.invalidateQueries({ queryKey: ['doctor', 'exams'] });

        const issuedResult = {
          type: activeTab,
          title: docTitle,
          link: digitalLink,
          unlockCode,
          id: memedId,
        };

        setIssuedDoc(issuedResult);

        toast({
          title: `${docTitle} emitida e assinada!`,
          description: 'Documento homologado pela MeMed com certificado digital ICP-Brasil.',
          variant: 'success',
        });

        onDocumentIssued?.(issuedResult);
      } catch (err) {
        console.error('[TelemedicinePrescriptionModal] Erro ao sincronizar documento MeMed:', err);
        toast({
          title: 'Documento emitido!',
          description: 'A MeMed emitiu a via oficial. Os dados foram salvos no atendimento.',
          variant: 'success',
        });
      }
    });

    const unsubscribeDeleted = onMemedEvent('prescricaoExcluida', async (idPrescricao) => {
      console.warn('[TelemedicinePrescriptionModal] Prescrição excluída na MeMed:', idPrescricao);
      try {
        await medicalRecordsApi.markMemedPrescriptionExcluded(String(idPrescricao));
        queryClient.invalidateQueries({ queryKey: ['medical-records'] });
        queryClient.invalidateQueries({ queryKey: ['doctor', 'prescriptions'] });
        setIssuedDoc(null);
        toast({
          title: 'Prescrição cancelada',
          description: 'O documento foi invalidado no prontuário do atendimento.',
          variant: 'info',
        });
      } catch (err) {
        console.error('Falha ao sincronizar exclusão do documento MeMed:', err);
      }
    });

    return () => {
      unsubscribePrinted();
      unsubscribeDeleted();
      if (typeof document !== 'undefined') {
        document.body.classList.remove('memed-in-telemedicine');
      }
    };
  }, [isOpen, activeTab, patient, doctorId, appointmentId, daysOff, certReason, certStartDate, cidCode, cidDescription, selectedExams, examJustification, rxType, onMemedEvent, onDocumentIssued, queryClient, toast]);

  const handleOpenMemed = useCallback(async () => {
    if (!patient) {
      setErrorMessage('Nenhum paciente selecionado para emissão.');
      return;
    }

    setErrorMessage(null);
    setIsOpeningMemed(true);
    if (typeof document !== 'undefined') {
      document.body.classList.add('memed-in-telemedicine');
    }

    try {
      // 1. Obter token MeMed do médico
      let token: string | undefined;
      try {
        const tokenRes = await memedApi.getToken();
        token = tokenRes?.token;
      } catch (tokenErr: any) {
        const msg = tokenErr?.message || 'Médico não está conectado à MeMed';
        setErrorMessage(`${msg}. Conecte sua conta MeMed em Configurações > MeMed para emitir com ICP-Brasil.`);
        setIsOpeningMemed(false);
        return;
      }

      if (!token) {
        setErrorMessage('Token MeMed não encontrado. Conecte sua conta em Configurações > MeMed.');
        setIsOpeningMemed(false);
        return;
      }

      // 2. Garantir script carregado
      if (!isScriptLoaded || !isModuleReady) {
        const scriptOk = await loadScript(token);
        if (!scriptOk) {
          setErrorMessage('Não foi possível carregar o módulo MeMed Sinapse. Tente novamente.');
          setIsOpeningMemed(false);
          return;
        }
      }

      // 3. Montar dados do paciente
      const memedPatient = mapPatientToMemed({
        id: patient.id,
        name: patient.name,
        gender: patient.gender,
        cpf: patient.cpf,
        birthDate: patient.birthDate,
        phone: patient.phone,
        email: patient.email,
        healthProfile: null,
      } as any);

      const validation = validateMemedPatient(memedPatient);
      if (!validation.valid) {
        setErrorMessage(`Dados cadastrais pendentes do paciente: ${validation.missingFields.join(', ')}.`);
        setIsOpeningMemed(false);
        return;
      }

      // 4. Preparar payload conforme aba ativa
      let items: any[] | undefined = undefined;
      let additionalData: any = undefined;

      if (activeTab === 'certificate') {
        const periodText = daysOff ? `${daysOff} dia(s) de repouso a partir de ${certStartDate}` : `repouso a partir de ${certStartDate}`;
        const cidText = cidCode ? ` • CID-10: ${cidCode}${cidDescription ? ` - ${cidDescription}` : ''}` : '';
        items = [{
          nome: `Atestado Médico — Afastamento (${daysOff || 1} dias)`,
          posologia: `Atesto para os devidos fins que o(a) paciente ${patient.name} necessita de ${periodText}. Justificativa: ${certReason.trim()}.${cidText}`,
          descricao: 'Atestado Médico',
        }];
      } else if (activeTab === 'exam') {
        items = selectedExams.map((examName) => ({
          nome: examName,
          posologia: examJustification.trim() ? `Indicação clínica: ${examJustification.trim()}` : 'Indicação: Avaliação diagnóstica de rotina',
          descricao: 'Exame complementar',
        }));
      } else {
        if (rxInstructions.trim()) {
          additionalData = { header: [{ Observações: rxInstructions.trim() }] };
        }
      }

      // 5. Acionar openPrescription da MeMed no mesmo ambiente
      const effectiveWorkplace = obterWorkplaceParaAtendimento(
        'TELEMEDICINA',
        memedConfig?.workplace,
      );

      const result = await openPrescription(
        memedPatient,
        effectiveWorkplace,
        [],
        [],
        { appointmentId: appointmentId || undefined },
        {
          receituarioTheme: rxType === 'CONTROLADA' ? 2 : 1,
          token,
          items,
          additionalData,
          featureToggles: {
            forceSign: false,
            buttonClose: true,
            deletePatient: false,
            removePatient: false,
            editPatient: false,
          },
        }
      );

      if (!result.success) {
        setErrorMessage(result.error || 'Erro ao abrir prescritor MeMed');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'Erro inesperado ao inicializar a MeMed');
    } finally {
      setIsOpeningMemed(false);
    }
  }, [patient, activeTab, rxType, rxInstructions, daysOff, certStartDate, certReason, cidCode, cidDescription, selectedExams, examJustification, isScriptLoaded, isModuleReady, loadScript, openPrescription, memedConfig, appointmentId]);

  const toggleExam = (exam: string) => {
    setSelectedExams((prev) =>
      prev.includes(exam) ? prev.filter((e) => e !== exam) : [...prev, exam]
    );
  };

  const handleAddCustomExam = () => {
    const trimmed = customExamInput.trim();
    if (trimmed && !selectedExams.includes(trimmed)) {
      setSelectedExams((prev) => [...prev, trimmed]);
      setCustomExamInput('');
    }
  };

  const handleCopyLink = () => {
    if (issuedDoc?.link) {
      navigator.clipboard.writeText(issuedDoc.link);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast({ title: 'Link copiado!', variant: 'success' });
    }
  };

  const safePatientName = patient?.name || 'Paciente';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 shadow-2xl">
        {/* Header do Modal */}
        <DialogHeader className="p-4 sm:p-5 pb-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-900/80">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-600/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <span>Emissão MeMed (ICP-Brasil)</span>
                  <span className="text-[10px] uppercase font-extrabold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                    Ao Vivo na Chamada
                  </span>
                </DialogTitle>
                <DialogDescription className="text-xs text-neutral-500 dark:text-neutral-400">
                  Paciente: <strong className="text-neutral-700 dark:text-neutral-200">{safePatientName}</strong>
                  {patient?.cpf ? ` • CPF: ${patient.cpf}` : ''}
                </DialogDescription>
              </div>
            </div>
          </div>

          {/* Abas Superiores */}
          <div className="grid grid-cols-3 gap-1.5 pt-3">
            <button
              type="button"
              onClick={() => { setActiveTab('prescription'); setIssuedDoc(null); }}
              className={`py-2 px-3 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'prescription'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700'
              }`}
            >
              <Pill className="w-3.5 h-3.5" />
              <span>Receita</span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('certificate'); setIssuedDoc(null); }}
              className={`py-2 px-3 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'certificate'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Atestado</span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('exam'); setIssuedDoc(null); }}
              className={`py-2 px-3 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                activeTab === 'exam'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700'
              }`}
            >
              <Activity className="w-3.5 h-3.5" />
              <span>Exames</span>
            </button>
          </div>
        </DialogHeader>

        {/* Conteúdo do Formulário */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-4 text-xs">
          {errorMessage && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl text-red-700 dark:text-red-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="flex-1 text-[11px] leading-relaxed">{errorMessage}</div>
            </div>
          )}

          {/* Feedback de Documento Emitido */}
          {issuedDoc && (
            <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl space-y-3">
              <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-bold text-sm">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span>{issuedDoc.title} assinada com sucesso!</span>
              </div>
              <p className="text-emerald-700 dark:text-emerald-400 text-xs">
                A assinatura ICP-Brasil foi validada pelo CFM/ITI e o documento já está disponível no prontuário.
              </p>
              {issuedDoc.link && (
                <div className="flex items-center gap-2 pt-1">
                  <a
                    href={issuedDoc.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-xs transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Abrir Via Digital Oficial</span>
                  </a>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="gap-1.5 text-xs"
                  >
                    {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedLink ? 'Copiado!' : 'Copiar Link'}</span>
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ABA 1: RECEITA MÉDICA */}
          {activeTab === 'prescription' && (
            <div className="space-y-3.5">
              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                  Tipo de Receituário
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRxType('COMUM')}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all ${
                      rxType === 'COMUM'
                        ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-300 font-semibold'
                        : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }`}
                  >
                    <div className="font-bold text-xs">Receita Simples</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">Medicamentos comuns e uso contínuo</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRxType('CONTROLADA')}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all ${
                      rxType === 'CONTROLADA'
                        ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-300 font-semibold'
                        : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }`}
                  >
                    <div className="font-bold text-xs">Controle Especial</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">Antimicrobianos e portaria 344 (C1/B1)</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                  Orientações Clínicas ou Observações (Opcional)
                </label>
                <textarea
                  value={rxInstructions}
                  onChange={(e) => setRxInstructions(e.target.value)}
                  placeholder="Instruções gerais para o paciente ou farmácia..."
                  className="w-full h-16 p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none dark:bg-neutral-800"
                />
              </div>

              <div className="bg-neutral-50 dark:bg-neutral-800/60 p-3 rounded-xl border border-neutral-200 dark:border-neutral-700 space-y-1 text-[11px] text-neutral-600 dark:text-neutral-400">
                <span className="font-bold text-neutral-800 dark:text-neutral-200 block">Como funciona na mesma tela:</span>
                <p>
                  Ao clicar no botão abaixo, o catálogo oficial da MeMed abrirá diretamente sobreposta a esta página.
                  Você poderá buscar medicamentos na base de 60.000 itens, ajustar posologia e assinar digitalmente com ICP-Brasil.
                </p>
              </div>
            </div>
          )}

          {/* ABA 2: ATESTADO MÉDICO */}
          {activeTab === 'certificate' && (
            <div className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                    Dias de Afastamento
                  </label>
                  <div className="flex gap-1.5 flex-wrap">
                    {[1, 2, 3, 5, 7, 14, 15].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDaysOff(d)}
                        className={`px-2.5 py-1 rounded-lg border text-xs font-semibold cursor-pointer transition-all ${
                          daysOff === d
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                        }`}
                      >
                        {d} {d === 1 ? 'dia' : 'dias'}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                    Data de Início
                  </label>
                  <input
                    type="date"
                    value={certStartDate}
                    onChange={(e) => setCertStartDate(e.target.value)}
                    className="w-full p-2 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none dark:bg-neutral-800"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                  Justificativa Clínica / Motivo do Atestado
                </label>
                <input
                  type="text"
                  value={certReason}
                  onChange={(e) => setCertReason(e.target.value)}
                  placeholder="Ex: Necessidade de repouso por quadro infeccioso respiratório agudo..."
                  className="w-full p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none dark:bg-neutral-800"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                    CID-10 (Opcional)
                  </label>
                  <input
                    type="text"
                    value={cidCode}
                    onChange={(e) => setCidCode(e.target.value.toUpperCase())}
                    placeholder="Ex: J06.9"
                    className="w-full p-2 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs font-mono uppercase focus:ring-1 focus:ring-emerald-500 focus:outline-none dark:bg-neutral-800"
                  />
                </div>
                <div className="col-span-2">
                  <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                    Descrição do CID
                  </label>
                  <input
                    type="text"
                    value={cidDescription}
                    onChange={(e) => setCidDescription(e.target.value)}
                    placeholder="Ex: Infecção aguda das vias aéreas superiores"
                    className="w-full p-2 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none dark:bg-neutral-800"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ABA 3: PEDIDO DE EXAMES */}
          {activeTab === 'exam' && (
            <div className="space-y-3.5">
              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                  Exames Selecionados ({selectedExams.length})
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedExams.map((exam) => (
                    <span
                      key={exam}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 text-xs font-semibold"
                    >
                      <span>{exam}</span>
                      <button
                        type="button"
                        onClick={() => toggleExam(exam)}
                        className="hover:text-red-600 cursor-pointer"
                        title="Remover exame"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {selectedExams.length === 0 && (
                    <span className="text-neutral-400 italic">Nenhum exame selecionado ainda.</span>
                  )}
                </div>

                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={customExamInput}
                    onChange={(e) => setCustomExamInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomExam(); } }}
                    placeholder="Digitar outro exame e pressionar Enter..."
                    className="flex-1 p-2 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none dark:bg-neutral-800"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddCustomExam}
                    disabled={!customExamInput.trim()}
                    className="gap-1 text-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Adicionar
                  </Button>
                </div>
              </div>

              <div>
                <span className="font-bold text-neutral-600 dark:text-neutral-400 text-[11px] block mb-1">
                  Sugestões Rápidas:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {COMMON_EXAMS.map((exam) => {
                    const isSelected = selectedExams.includes(exam);
                    return (
                      <button
                        key={exam}
                        type="button"
                        onClick={() => toggleExam(exam)}
                        className={`px-2 py-0.5 rounded-md text-[11px] font-medium border cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                        }`}
                      >
                        {isSelected ? '✓ ' : '+ '}{exam}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="font-bold text-neutral-700 dark:text-neutral-300 block mb-1">
                  Indicação Clínica / Hipótese Diagnóstica
                </label>
                <input
                  type="text"
                  value={examJustification}
                  onChange={(e) => setExamJustification(e.target.value)}
                  placeholder="Ex: Investigação de síndrome anêmica, rotina diagnóstica..."
                  className="w-full p-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none dark:bg-neutral-800"
                />
              </div>
            </div>
          )}
        </div>

        {/* Rodapé com Ação Principal */}
        <div className="p-4 sm:p-5 pt-3 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-900/80 flex flex-col sm:flex-row items-center justify-between gap-2.5">
          <div className="text-[11px] text-neutral-500 dark:text-neutral-400 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <span>Assinatura digital padrão ICP-Brasil homologada CFM</span>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="flex-1 sm:flex-initial text-xs"
            >
              Fechar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleOpenMemed}
              disabled={isOpeningMemed}
              className="flex-1 sm:flex-initial gap-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
            >
              {isOpeningMemed ? (
                <>
                  <Clock className="w-3.5 h-3.5 animate-spin" />
                  <span>Carregando MeMed...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>
                    {activeTab === 'prescription'
                      ? 'Prescrever e Assinar via MeMed'
                      : activeTab === 'certificate'
                      ? 'Emitir Atestado via MeMed'
                      : 'Solicitar Exames via MeMed'}
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
